import { useState, useEffect, useMemo } from "react";
import { formatCurrency, useEscapeClose } from "../../../utils/SamuiUtils";
import { useModalKeyboardNavigation, getKeyboardInstructions } from '../../../utils/ModalKeyboardNavigation';

const getItemUniqueKey = (item) =>
  item?.Item_Code ? item.Item_Code.toString().trim().toLowerCase() : item?.Item_Id;

const ItemForSaleModal = ({
  showItemModal,
  handleItemClose,
  itemDataList,
  onRowSelectItem,
  itemPriceType,
  setItemPriceType
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredItemDataList, setFilteredItemDataList] =
    useState(itemDataList);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedRowIndex, setSelectedRowIndex] = useState(0); // เพิ่ม state สำหรับเลือกแถว

  const uniqueItemDataList = useMemo(() => {
    const itemMap = new Map();

    itemDataList.forEach((item) => {
      const itemKey = getItemUniqueKey(item);
      if (itemKey && !itemMap.has(itemKey)) {
        itemMap.set(itemKey, item);
      }
    });

    return Array.from(itemMap.values());
  }, [itemDataList]);

  const filteredItemKeys = useMemo(
    () => new Set(filteredItemDataList.map((item) => getItemUniqueKey(item))),
    [filteredItemDataList]
  );

  const isItemSelected = (item) =>
    selectedItems.some(
      (selected) => getItemUniqueKey(selected) === getItemUniqueKey(item)
    );

  const areAllFilteredItemsSelected =
    filteredItemDataList.length > 0 &&
    filteredItemDataList.every((item) => isItemSelected(item));

  const getSelectedItemsForConfirm = (highlightedItem) => {
    if (!highlightedItem || isItemSelected(highlightedItem)) {
      return selectedItems;
    }

    return [...selectedItems, highlightedItem];
  };

  useEscapeClose(showItemModal, handleItemClose);

  // เพิ่ม keyboard navigation สำหรับ checkbox modal
  useModalKeyboardNavigation(
    showItemModal,
    filteredItemDataList,
    selectedRowIndex,
    setSelectedRowIndex,
    null, // ไม่ใช้ onRowSelect เพราะเป็น checkbox
    handleItemClose,
    {
      customSpaceHandler: () => {
        // Space bar สำหรับ toggle checkbox
        if (filteredItemDataList.length > 0 && selectedRowIndex < filteredItemDataList.length) {
          handleCheckboxChange(filteredItemDataList[selectedRowIndex]);
        }
      },
      customEnterHandler: (highlightedItem) => {
        // Enter สำหรับยืนยันการเลือก
        onRowSelectItem(getSelectedItemsForConfirm(highlightedItem));
      }
    }
  );

  useEffect(() => {
    if (showItemModal) {
      setSearchTerm("");
      setSelectedItems([]);
    }
  }, [showItemModal]);

  useEffect(() => {
    const nextFilteredItemDataList = uniqueItemDataList.filter(
      (item) =>
        item.Item_Code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.Item_Name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredItemDataList(nextFilteredItemDataList);
    setSelectedRowIndex(0);
  }, [searchTerm, uniqueItemDataList]);

  const handleSelectAll = (isChecked) => {
    if (isChecked) {
      setSelectedItems((prevSelected) => {
        const selectedKeySet = new Set(
          prevSelected.map((item) => getItemUniqueKey(item))
        );
        const itemsToAdd = filteredItemDataList.filter(
          (item) => !selectedKeySet.has(getItemUniqueKey(item))
        );

        return [...prevSelected, ...itemsToAdd];
      });
    } else {
      setSelectedItems((prevSelected) =>
        prevSelected.filter(
          (item) => !filteredItemKeys.has(getItemUniqueKey(item))
        )
      );
    }
  };

  const handleCheckboxChange = (item) => {
    setSelectedItems((prevSelected) => {
      const isSelected = prevSelected.some(
        (selected) => getItemUniqueKey(selected) === getItemUniqueKey(item)
      );
      return isSelected
        ? prevSelected.filter(
          (selected) => getItemUniqueKey(selected) !== getItemUniqueKey(item)
        )
        : [...prevSelected, item];
    });
  };

  return (
    <>
      <div
        className={`modal ${showItemModal ? "show" : ""}`}
        style={{ display: showItemModal ? "block" : "none" }}
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-xl" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">รายชื่อสินค้า</h5>
              <button type="button" className="close" onClick={handleItemClose}>
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <div className="row">
                <div className={`col-${localStorage.getItem("company") === "1" ? "6" : "12"}`}>
                  <div className="form-group">
                    <span className="fw-bold">ค้นหาสินค้า</span>
                    <input
                      style={{ width: '100%' }}
                      type="text"
                      className="form-control"
                      placeholder="ค้นหา รหัสสินค้า หรือ ชื่อสินค้า"
                      value={searchTerm || ""}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                {localStorage.getItem("company") === "1" && (
                  <div className="col-6">
                    <div className="form-group">
                      <span className="fw-bold">ประเภทราคา</span>
                      <div className="radio-inline">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="priceType"
                          value="IP"
                          id="priceTypeIP"
                          checked={itemPriceType === "IP"}
                          onChange={() => setItemPriceType("IP")}
                          disabled={localStorage.getItem("company") === "2"}
                        />
                        <label className="form-check-label ml-2" htmlFor="priceTypeIP">ราคาขาย</label>
                        <input
                          className="form-check-input"
                          type="radio"
                          name="priceType"
                          value="IWP"
                          id="priceTypeIWP"
                          checked={itemPriceType === "IWP"}
                          onChange={() => setItemPriceType("IWP")}
                          disabled={localStorage.getItem("company") === "2"}
                        />
                        <label className="form-check-label ml-2" htmlFor="priceTypeIWP">ราคาขายส่ง</label>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div
                className="table-responsive"
                style={{ maxHeight: "650px", overflowY: "auto" }}
              >
                <table className="table table-striped table-hover">
                  <thead className="thead-dark">
                    <tr>
                      <th className="text-center" style={{ width: "2%" }}>
                        <input
                          type="checkbox"
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          checked={areAllFilteredItemsSelected}
                        />
                      </th>
                      <th className="text-center" style={{ width: "18%" }}>
                        รหัสสินค้า
                      </th>
                      <th className="text-center" style={{ width: "40%" }}>
                        ชื่อสินค้า
                      </th>
                      <th className="text-center" style={{ width: "10%" }}>
                        ราคาขาย
                      </th>
                      {localStorage.getItem("company") === "1" && (
                        <th className="text-center" style={{ width: "10%" }}>
                          ราคาขายส่ง
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItemDataList.length > 0 ? (
                      filteredItemDataList.map((item, index) => (
                        <tr
                          key={`${getItemUniqueKey(item) || "item"}-${index}`}
                          onClick={() => {
                            setSelectedRowIndex(index);
                            handleCheckboxChange(item);
                          }}
                          style={{
                            cursor: "pointer",
                            backgroundColor: index === selectedRowIndex 
                              ? "rgb(105, 188, 216)" 
                              : isItemSelected(item)
                                ? "rgba(105, 188, 216, 0.3)"
                                : "transparent",
                            transition: "background-color 0.3s ease",
                          }}
                        >
                          <td className="text-center">
                            <input
                              type="checkbox"
                              checked={isItemSelected(item)}
                              onChange={() => handleCheckboxChange(item)}
                              onClick={(e) => e.stopPropagation()}
                              style={{ cursor: "pointer" }}
                            />
                          </td>
                          <td className="text-center">{item.Item_Code}</td>
                          <td className="text-left">{item.Item_Name}</td>
                          <td className="text-end">{formatCurrency(item.Item_Price)}</td>
                          {localStorage.getItem("company") === "1" && (
                            <td className="text-end">{formatCurrency(item.Item_Wholesale_price)}</td>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center">
                          ไม่พบข้อมูล
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <div className="text-left" style={{ fontSize: "12px", color: "#666", marginRight: "auto" }}>
                ↑↓: เลื่อน | Space: เลือก/ยกเลิก | Enter: ยืนยัน | ESC: ปิด
              </div>
              
              <button
                className="btn btn-primary"
                onClick={() => {
                  onRowSelectItem(selectedItems);
                }}
              >
                ยืนยัน
              </button>
              <button className="btn btn-danger" onClick={handleItemClose}> ปิด (ESC)</button>
            </div>
          </div>
        </div>
      </div>
      {showItemModal && <div className="modal-backdrop fade show"></div>}
    </>
  );
};

export default ItemForSaleModal;
